import { useCallback } from 'react';
import { NavigateFunction, NavigateOptions, To, useLocation, useNavigate } from 'react-router-dom';

// A/B parallel funnel (DEV-4731): the DZO funnel is registered twice — the baseline under
// `dopunsko-zdravstveno-osiguranje` and the v2.5 cohort under `dopunsko-zdravstveno-osiguranje-b`.
// Visitors are bucketed into the `-b` base by the external A/B tool (VWO/Omniconvert) at the
// step-1 entry. This hook is a drop-in replacement for react-router's `useNavigate` that keeps a
// `-b` visitor on the `-b` base for every in-app navigation, so the two flows never cross URLs.
// On the baseline (non-`-b`) funnel it behaves exactly like `useNavigate`.

const A_BASE = 'dopunsko-zdravstveno-osiguranje';
const B_BASE = 'dopunsko-zdravstveno-osiguranje-b';

// True only for the `-b` base segment — `-brzipregled` and the `dodatno` funnel never match.
const isBPath = (pathname: string) => pathname.includes(`${B_BASE}/`) || pathname.endsWith(B_BASE);

// Insert `-b` into the base segment of a target path. No-op when already `-b`, when there is no
// clean base segment (e.g. `-brzipregled` or a `dodatno` path), or for non-funnel targets.
const toBPath = (path: string): string => {
  if (isBPath(path)) return path;
  if (path.includes(`${A_BASE}/`)) return path.replace(`${A_BASE}/`, `${B_BASE}/`);
  if (path.endsWith(A_BASE)) return `${path.slice(0, -A_BASE.length)}${B_BASE}`;
  return path;
};

export const useFunnelNavigate = (): NavigateFunction => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onB = isBPath(pathname);

  return useCallback((to: To | number, options?: NavigateOptions) => {
    if (!onB || typeof to === 'number') {
      return navigate(to as To, options);
    }
    if (typeof to === 'string') {
      return navigate(toBPath(to), options);
    }
    return navigate({ ...to, pathname: to.pathname ? toBPath(to.pathname) : to.pathname }, options);
  }, [navigate, onB]) as NavigateFunction;
};
